import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  Chip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import Autocomplete from '@mui/lab/Autocomplete';
import { Text } from 'shared/components';

const IndicatorDrawer = ({
  tags,
  services,
  // benchmarkTypes,
  measurementTypes,
  stages,
  onSave,
  selected,
  requestDelete,
}) => {
  const isMulti = !!(selected?.id && Array.isArray(selected.id));

  const { values, setFieldValue, errors, handleSubmit, ...rest } = useFormik({
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      id: null,
      name: '',
      stageId: null,
      definition: '',
      guidance: '',
      // benchmarkType: null,
      measurementTypeId: null,
      tagList: [],
      servicesList: [],
      indicators: [],
      closed: false,
    },
    onSubmit: (values, { resetForm }) => {
      values.customizable = !values.closed;
      onSave(
        {
          ...values,
          tagList: JSON.stringify(values.tagList),
          servicesList: JSON.stringify(values.servicesList.map(se => se.dbId)),
        },
        resetForm,
      );
    },
    validate: values => {
      const errors = {};
      const multiEdit = !!(values?.id && Array.isArray(values.id));
      if (!multiEdit && !values.name) {
        errors.name = 'A name is required';
      }
      if (!multiEdit && !values.stageId) {
        errors.stageId = 'A stage is required';
      }
      if (!multiEdit && !values.definition) {
        errors.definition = 'A definition is required';
      }
      return errors;
    },
  });

  useEffect(() => {
    if (selected) {
      setFieldValue('id', selected.id || selected.dbId);
      setFieldValue('name', selected.name);
      setFieldValue('stageId', selected.stageId);
      setFieldValue('definition', selected.definition);
      setFieldValue('guidance', selected.guidance);
      // setFieldValue('benchmarkType', selected.benchmarkType);
      setFieldValue('measurementTypeId', selected.measurementTypeId);
      setFieldValue(
        'tagList',
        (selected?.tags || []),
      );
      setFieldValue(
        'servicesList',
        (selected?.services || []).map(se => ({ name: se.name, id: se.dbId })),
      );
      setFieldValue('closed', !selected.customizable);
      if (selected.id) {
        setFieldValue('indicators', selected.indicators);
      }
    }
  }, [selected]);

  const inAllTags = name => {
    if (!isMulti) return true;
    let isIn = 0;
    values.indicators.forEach(indicator => {
      if (indicator.tags.filter(tag => tag.tag === name).length > 0) isIn += 1;
    });
    return isIn === 0 || isIn === values.indicators.length;
  };

  const inAllServices = name => {
    if (!isMulti) return true;
    let isIn = 0;
    values.indicators.forEach(indicator => {
      if (
        indicator.services.filter(service => service.name === name).length > 0
      )
        isIn += 1;
    });
    return isIn === 0 || isIn === values.indicators.length;
  };

  const displayId = isMulti
    ? (values.id || []).reduce((acc, item, index) => {
        acc += item;
        if (index < values.id.length - 1) acc += ', ';
        return acc;
      }, '')
    : values.id;

  return (
    <Grid container direction="row" justify="center" alignItems="center">
      {values.id && (
        <Grid item xs={12} style={{ marginTop: '10px' }}>
          <Text variant="body1">
            ID: <b>{displayId}</b>
          </Text>
        </Grid>
      )}
      <Grid items xs={12}>
        Name:
      </Grid>
      <Grid item xs={12}>
        <TextField
          placeholder={isMulti ? '< multiple values >' : ''}
          variant="outlined"
          size="small"
          fullWidth
          value={values.name}
          onChange={e => setFieldValue('name', e.target.value)}
          error={errors.name}
          helperText={errors.name}
          disabled={isMulti}
          style={isMulti ? { backgroundColor: '#e8e8e8' } : {}}
        />
      </Grid>
      <Grid items xs={2}>
        Stage
      </Grid>
      <Grid item xs={10}>
        <TextField
          placeholder={isMulti ? '< multiple values >' : ''}
          select={!isMulti}
          fullWidth
          value={values.stageId}
          defaultValue={values.stageId}
          onChange={(_, { props }) => setFieldValue('stageId', props.value)}
          error={errors.stageId}
          disabled={isMulti}
          style={isMulti ? { backgroundColor: '#e8e8e8' } : {}}
        >
          {stages.map(item => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid items xs={12}>
        Definition:
      </Grid>
      <Grid item xs={12}>
        <TextField
          placeholder={isMulti ? '< multiple values >' : ''}
          variant="outlined"
          size="small"
          fullWidth
          value={values.definition}
          onChange={e => setFieldValue('definition', e.target.value)}
          error={errors.definition}
          multiline
          rows={3}
          helperText={errors.definition}
          disabled={isMulti}
          style={isMulti ? { backgroundColor: '#e8e8e8' } : {}}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={values.closed}
              onChange={e => setFieldValue('closed', e.target.checked)}
              color="primary"
            />
          }
          label="Closed (Can’t be edited by Grantee)"
        />
      </Grid>
      <Grid items xs={12}>
        Guidance:
      </Grid>
      <Grid item xs={12}>
        <TextField
          placeholder={isMulti ? '< multiple values >' : ''}
          variant="outlined"
          size="small"
          fullWidth
          value={values.guidance}
          onChange={e => setFieldValue('guidance', e.target.value)}
          error={errors.guidance}
          multiline
          rows={3}
          helperText={errors.guidance}
          disabled={isMulti}
          style={isMulti ? { backgroundColor: '#e8e8e8' } : {}}
        />
      </Grid>
      {/* <Grid items xs={4}>
        Benchmark Type
      </Grid>
      <Grid item xs={8}>
        <TextField
          select
          fullWidth
          value={values.benchmarkType}
          defaultValue={values.benchmarkType}
          onChange={(_, { props }) =>
            setFieldValue('benchmarkType', props.value)
          }
          error={errors.benchmarkType}
        >
          {benchmarkTypes.map(item => (
            <MenuItem key={item.name} value={item.name}>
              {item.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid> */}
      <Grid items xs={4}>
        Measurement Type
      </Grid>
      <Grid item xs={8}>
        <TextField
          select
          fullWidth
          value={values.measurementTypeId}
          defaultValue={values.measurementTypeId}
          onChange={(_, { props }) =>
            setFieldValue('measurementTypeId', props.value)
          }
          error={errors.measurementTypeId}
        >
          {measurementTypes.map(item => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid items xs={12}>
        Investment Tags:
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          options={tags}
          value={values.tagList}
          onChange={(_, value) => setFieldValue('tagList', value)}
          getOptionLabel={option => option}
          freeSolo
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              label={`Tags: ${values.tagList.length} selected`}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                color={inAllTags(option) ? 'primary' : 'default'}
                label={option}
                {...getTagProps({ index })}
              />
            ))
          }
          multiple
        />
      </Grid>
      <Grid items xs={12}>
        Impact Models:
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          options={services}
          value={values.servicesList}
          onChange={(_, value) => setFieldValue('servicesList', value)}
          getOptionLabel={option => option.name}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              label={`Impact Models: ${values.servicesList.length} selected`}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                color={inAllServices(option.name) ? 'primary' : 'default'}
                label={option.name}
                {...getTagProps({ index })}
              />
            ))
          }
          multiple
        />
      </Grid>
      <Grid item xs={6} style={{ marginTop: '10px' }}>
        {values.id && (
          <Button
            onClick={() => requestDelete(selected)}
            style={{ float: 'left' }}
          >
            DELETE INDICATOR
          </Button>
        )}
      </Grid>
      <Grid item xs={6} style={{ marginTop: '10px' }}>
        <Button onClick={handleSubmit} style={{ float: 'right' }}>
          SAVE CHANGES
        </Button>
      </Grid>
    </Grid>
  );
};

export default IndicatorDrawer;
